import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { usersAPI, assetsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { User, Code, TrendingUp, CheckCircle2, Loader2 } from 'lucide-react';

type Role = 'investor' | 'researcher' | 'dev';

const ROLE_INFO: Record<Role, { label: string; icon: React.ReactNode; description: string }> = {
  investor: {
    label: 'Investor',
    icon: <TrendingUp className="w-8 h-8" />,
    description: '투자 의사결정을 위한 가치평가 데이터를 활용합니다.',
  },
  researcher: {
    label: 'Researcher',
    icon: <Code className="w-8 h-8" />,
    description: '모델을 개발하고 백테스트하여 연구합니다.',
  },
  dev: {
    label: 'Protocol Developer',
    icon: <User className="w-8 h-8" />,
    description: '프로토콜 개발 및 최적화를 위한 데이터를 활용합니다.',
  },
};

export function Onboarding() {
  const navigate = useNavigate();
  const { user, setAuth } = useAuthStore();
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [demoDataAllowed, setDemoDataAllowed] = useState(false);

  // 자산 목록 조회
  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ['assets', 'onboarding'],
    queryFn: () => assetsAPI.list({ limit: 100 }),
  });

  const assets = assetsData?.data?.items || assetsData?.data || [];

  // 온보딩 완료
  const completeOnboarding = useMutation({
    mutationFn: usersAPI.completeOnboarding,
    onSuccess: (response: any) => {
      if (response?.status === 'ok') {
        // 사용자 정보 다시 조회하여 업데이트
        usersAPI.getProfile().then((profileResponse: any) => {
          if (profileResponse?.status === 'ok' && profileResponse?.data) {
            const token = localStorage.getItem('token');
            if (token) {
              setAuth(profileResponse.data, token);
            }
          }
        });
        navigate('/dashboard');
      }
    },
  });

  const handleNext = () => {
    if (step === 1 && !selectedRole) {
      return;
    }
    if (step === 2) {
      // 온보딩 완료
      completeOnboarding.mutate({
        role: selectedRole || undefined,
        interested_assets: selectedAssets,
        demo_data_allowed: demoDataAllowed,
      });
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const toggleAsset = (assetId: string) => {
    if (selectedAssets.includes(assetId)) {
      setSelectedAssets(selectedAssets.filter((id) => id !== assetId));
    } else {
      if (selectedAssets.length < 10) {
        setSelectedAssets([...selectedAssets, assetId]);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 p-4">
      <div className="w-full max-w-2xl">
        <div className="card">
          {/* 진행 표시 */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">
                Step {step} of 2
              </span>
              <span className="text-sm font-medium text-primary-600">
                {Math.round((step / 2) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 2) * 100}%` }}
              />
            </div>
          </div>

          {/* Step 1: Role 선택 */}
          {step === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                역할을 선택해주세요
              </h2>
              <p className="text-gray-600 mb-6">
                귀하의 역할에 맞는 기능과 데이터를 제공합니다.
              </p>

              <div className="space-y-4">
                {(Object.keys(ROLE_INFO) as Role[]).map((role) => (
                  <button
                    key={role}
                    onClick={() => setSelectedRole(role)}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      selectedRole === role
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-start">
                      <div
                        className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                          selectedRole === role
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {ROLE_INFO[role].icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {ROLE_INFO[role].label}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {ROLE_INFO[role].description}
                        </p>
                      </div>
                      {selectedRole === role && (
                        <CheckCircle2 className="w-6 h-6 text-primary-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: 관심자산 선택 및 데모 데이터 */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                관심 자산을 선택해주세요
              </h2>
              <p className="text-gray-600 mb-6">
                최대 10개의 자산을 선택할 수 있습니다. 선택한 자산은 Watchlist에 자동으로 추가됩니다.
              </p>

              {assetsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <>
                  <div className="mb-6 max-h-96 overflow-y-auto">
                    <div className="grid grid-cols-1 gap-2">
                      {assets.map((asset: any) => (
                        <button
                          key={asset.id}
                          onClick={() => toggleAsset(asset.id)}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            selectedAssets.includes(asset.id)
                              ? 'border-primary-600 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {asset.symbol}
                              </h3>
                              <p className="text-sm text-gray-600">{asset.name}</p>
                            </div>
                            {selectedAssets.includes(asset.id) && (
                              <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={demoDataAllowed}
                        onChange={(e) => setDemoDataAllowed(e.target.checked)}
                        className="mt-1 mr-3 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <span className="font-medium text-gray-900">
                          데모 데이터 사용 허용
                        </span>
                        <p className="text-sm text-gray-600 mt-1">
                          샘플 데이터와 데모 리포트를 제공받을 수 있습니다.
                        </p>
                      </div>
                    </label>
                  </div>

                  {selectedAssets.length > 0 && (
                    <div className="mb-6 p-3 bg-primary-50 rounded-lg">
                      <p className="text-sm text-primary-700">
                        <strong>{selectedAssets.length}개</strong>의 자산이 선택되었습니다.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* 버튼 */}
          <div className="flex justify-between mt-8">
            <button
              onClick={handleBack}
              disabled={step === 1}
              className="btn btn-secondary"
            >
              이전
            </button>
            <button
              onClick={handleNext}
              disabled={
                (step === 1 && !selectedRole) ||
                completeOnboarding.isPending
              }
              className="btn btn-primary"
            >
              {completeOnboarding.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  처리 중...
                </>
              ) : step === 2 ? (
                '완료'
              ) : (
                '다음'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

